<script setup>
import {
    reactive,
    ref,
    computed,
    onMounted,
    watch,
    onBeforeUnmount,
} from "vue";
import { useLoaderStore } from "@store/loader";
import { ElNotification } from "element-plus";
import ClientsApi from "@api/clients.api";
import useForm from "@composables/Form";
import { useValidation } from "@composables/useValidation";
import { required, maxLength } from "@services/i18n-validators";
import Swal from "sweetalert2";

const props = defineProps({
    modelValue: { type: Boolean, default: false },
    industriesForSelect: {
        type: Array,
        default: () => [],
    },
    clientTypesForSelect: {
        type: Array,
        default: () => [],
    },
});
const emit = defineEmits(["update:modelValue", "created"]);

const loaderStore = useLoaderStore();
let { formErrors, catchErrors } = useForm();

const client = reactive({
    name: "",
    abbreviation: "",
    camp: "",
    website_url: "",
    industry_id: null,
    client_type_id: null,
});

// Validation rules (same pattern as Create.vue)
const rules = reactive({
    website_url: { required, maxLength: maxLength(255) },
    name: { required, maxLength: maxLength(255) },
    industry_id: { required },
    client_type_id: { required },
    camp: {  maxLength: maxLength(255) },
    abbreviation: { required, maxLength: maxLength(255) },
});
const { v$, $externalResults } = useValidation(rules, client);
// Client-side URL validation (protocol optional)
const validateUrl = (value) => {
    if (!value) return true;
    const regex = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/;
    return regex.test(String(value).trim());
};

function resetForm() {
    client.name = "";
    client.abbreviation = "";
    client.camp = "";
    client.website_url = "";
    client.industry_id = null;
    client.client_type_id = null;
    formErrors.value = {};
    if ($externalResults && $externalResults.value) $externalResults.value = {};
    if (v$ && v$.value) v$.value.$reset();
}

function close() {
    resetForm();
    document.getElementsByName("el-overlay-dialog").forEach((el) => {
        el.classList.remove("el-overlay-dialog-c");
    });

    emit("update:modelValue", false);
}

async function submit() {
    try {
        const isValid = await v$.value.$validate();
        if (!validateUrl(client.website_url)) {
            formErrors.value = {
                ...formErrors.value,
                website_url: ["Invalid URL format (e.g. https://example.com)"],
            };
            return;
        }

        if (loaderStore.start) loaderStore.start();
        const { data } = await ClientsApi.store(client);
        ElNotification({
            title: "Success",
            message: data.message || "Client created",
            type: "success",
            position: "bottom-left",
        });
        const created = data?.data?.client || data?.data;
        emit("created", created);
        close();
    } catch (e) {
        const backendErrors = e?.response?.data?.errors;
        if (backendErrors && typeof backendErrors === "object") {
            $externalResults.value = backendErrors;
            catchErrors(backendErrors);

            const html = `<ul style="text-align:left;margin:0;padding-left:1.2rem;">${Object.entries(
                backendErrors
            )
                .map(
                    ([k, v]) =>
                        `<li><b>${k}</b>: ${
                            Array.isArray(v) ? v.join(", ") : v
                        }</li>`
                )
                .join("")}</ul>`;

            return;
        }
    } finally {
        if (loaderStore.stop) loaderStore.stop();
    }
}

// While this modal is open, temporarily suspend wide-modal CSS on parent dialogs
watch(
    () => props.modelValue,
    (isOpen) => {
        const cls = "suspend-wide-dialog";
        if (isOpen) {
            document.body.classList.add(cls);
            resetForm(); // Reset form when modal opens
        } else {
            document.body.classList.remove(cls);
        }
    },
    { immediate: true }
);

onBeforeUnmount(() => {
    document.body.classList.remove("suspend-wide-dialog");
});

onMounted(() => {
    document
        .querySelector('div[aria-label="Add New Client"]')
        .classList.add("el-overlay-dialog-c");
});

// Normalize errors for selects: merge server errors with first Vuelidate message
const selectErrors = computed(() => {
    const result = { ...($externalResults?.value || {}) };
    const v = v$?.value;
    if (v) {
        const firstMessage = (field) =>
            Array.isArray(v?.[field]?.$errors) && v[field].$errors.length
                ? [v[field].$errors[0].$message]
                : undefined;
        const industryErr = firstMessage("industry_id");
        if (industryErr) result.industry_id = industryErr;
        const typeErr = firstMessage("client_type_id");
        if (typeErr) result.client_type_id = typeErr;
    }
    return result;
});
</script>
<template>
    <el-dialog
        :model-value="modelValue"
        :title="'Add New Client'"
        width="720px"
        class="request-doc-dialog client-dialog standalone-dialog"
        align-center
        @close="close"
        destroy-on-close
    >
        <div class="row m-0">
            <!-- website url -->
            <div class="col-md-12 p-0">
                <form-input
                    type="text"
                    v-model="client.website_url"
                    id="client_website_url"
                    :label="$t('fields.client_website')"
                    :required="true"
                    :errors="v$?.website_url"
                    :placeholder="$t('placeholders.website_url_placeholder')"
                />
            </div>
            <!-- name -->
            <div class="col-md-12 p-0">
                <form-input
                    type="text"
                    v-model="client.name"
                    id="client_name"
                    label="Client name"
                    :required="true"
                    :errors="v$?.name"
                    :placeholder="$t('placeholders.client_name_placeholder')"
                />
            </div>
            <!-- industry -->
            <div class="col-md-12 mb-3 p-0">
                <form-el-select
                    name="industry_id"
                    :options="industriesForSelect"
                    label="Client Industry"
                    :model="client"
                    :errors="selectErrors"
                    label-field="fields.industry_id"
                    :required="true"
                    @change="(val) => (client.industry_id = val)"
                    clearable
                    :placeholder="$t('placeholders.industr_placeholder')"
                />
            </div>

            <!-- camp -->
            <div class="col-md-12 p-0">
                <form-input
                    type="text"
                    v-model="client.camp"
                    id="client_camp"
                    label="Client Camp"
                    :required="false"
                    :errors="v$?.camp"
                    :placeholder="$t('placeholders.camp_placeholder')"
                />
            </div>
            <!-- abbreviation -->
            <div class="col-md-12 p-0">
                <form-input
                    type="text"
                    v-model="client.abbreviation"
                    id="client_abbreviation"
                    label="Client Abbreviation"
                    :required="true"
                    :errors="v$?.abbreviation"
                    :placeholder="$t('placeholders.abbreviation_placeholder')"
                />
            </div>

            <!-- client_type -->
            <div class="col-md-12 mb-3 p-0">
                <form-el-select
                    name="client_type_id"
                    :options="clientTypesForSelect"
                    label="Client Type"
                    :model="client"
                    :errors="selectErrors"
                    label-field="fields.client_type_id"
                    :required="true"
                    @change="(val) => (client.client_type_id = val)"
                    clearable
                    :placeholder="$t('placeholders.client_type_placeholder')"
                />
            </div>
        </div>

        <template #footer>
            <div class="dialog-footer d-flex gap-2 justify-content-between">
                <button type="button" class="btn btn-light" @click="close">
                    {{ $t ? $t("messages.cancel") : "Cancel" }}
                </button>
                <button type="button" class="btn btn-primary" @click="submit">
                    {{ $t ? $t("messages.add") : "Add" }}
                </button>
            </div>
        </template>
    </el-dialog>
</template>
<style scoped>
.client-dialog :deep(.el-dialog) {
    border-radius: 16px;
    margin: 0 auto !important;
}

.client-dialog :deep(.el-dialog__header) {
    font-weight: 700;
}

.dialog-footer .btn {
    min-width: 120px;
    height: 44px;
    border-radius: 10px;
}

/* Center modal and constrain height with internal scrolling */

.client-dialog :deep(.el-dialog__wrapper) {
    display: flex;
    align-items: center;
    justify-content: center;
}

.client-dialog :deep(.el-dialog__body) {
    max-height: 70vh;
    overflow-y: auto;
}

/* Ensure this dialog ignores wide-modal global sizing */
.standalone-dialog :deep(.el-dialog) {
    width: 720px !important;
    max-width: 96vw !important;
    height: auto !important;
    margin: 0 auto !important;
}

.standalone-dialog :deep(.el-dialog__body) {
    height: auto !important;
    max-height: 70vh !important;
}

:deep(.el-overlay-dialog) {
    bottom: unset !important;
    padding: 10% 20%;
}
</style>
